import {
  ArrowRightOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined
} from "@ant-design/icons";
import { PageHeader } from "@mj/shared-ui";
import { Button, message, Modal, Skeleton, Steps, Typography } from "antd";
import { AxiosError } from "axios";
import dayjs from "dayjs";
import React from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import Layout from "@/components/common/Layout";
import { AddCostDurationStep } from "@/components/forecasting/AddCostDurationStep";
import { AddStagesStep } from "@/components/forecasting/AddStagesStep";
import { AllocateTasksStep } from "@/components/forecasting/AllocateTasksStep";
import { getForecastTotal } from "@/components/forecasting/forecastTotals";
import { SelectRunPlotStep } from "@/components/forecasting/SelectRunPlotStep";
import { SummaryStep } from "@/components/forecasting/SummaryStep";
import {
  flattenAllocations,
  getTaskAllocationKey,
  groupAllocationsByWeek,
  pruneAllocations,
  WeeklyAllocations
} from "@/components/forecasting/taskAllocations";
import { useUser } from "@/contexts/UserContext";
import {
  ICarryForwardState,
  useForecastCostPrefill
} from "@/features/forecast-carry-forward";
import {
  useGetMonthlyForecast,
  useGetProjectCentralRepoWithOpportunityNo,
  useCreateMonthlyForecast,
  useCreateNewMonthlyForecastVersion,
  useLockUnlockForecast
} from "@/hooks/forecasting";
import { useGetProjectJobTypes } from "@/hooks/projectJobTypes";
import { useConfirmLeave } from "@/hooks/useConfirmLeave";
import { getLabourDayRate } from "@/utils/labour";
import { getMaterialPercent } from "@/utils/material";

const { Text } = Typography;

export interface IBulkStageSelection {
  stageId: string;
  plotNumbers: string[];
}

export interface ILabourCostEntry {
  id: string;
  name: string;
  labourTrade: string;
  ratePerHour: number;
  hoursPerDay: number;
  noOfDays: number;
  amount: number;
}

/** One row per plant category — `id` and `name` are both the category name. */
export interface IPlantCostEntry {
  id: string;
  name: string;
  unitOfMeasure: string;
  unitCost: number;
  quantity: number;
  noOfDays: number;
  amount: number;
}

/** The percent of the estimate is derived when needed — see `getMaterialPercent`. */
export interface IMaterialCostEntry {
  amount: number;
}

export interface ISubcontractorCostEntry {
  /** BC subcontractor `no`. */
  name: string;
  /** Trade the subcontractor list is filtered by. UI-only, not saved. */
  tradeCode: string;
  amount: number;
}

export interface IProjectCostInput {
  estimatedLabourCost: number;
  estimatedPlantCost: number;
  estimatedMaterialCost: number;
  estimatedSubcontractorCost: number;
  labourCosts: ILabourCostEntry[];
  plantCosts: IPlantCostEntry[];
  materialCosts: IMaterialCostEntry[];
  subcontractorCosts: ISubcontractorCostEntry[];
}

export interface ITaskAllocation {
  /**
   * `getTaskAllocationKey` for this allocation, cached on creation — the flow
   * can hold 100k+ allocations and rebuilding the key per render is the
   * difference between a responsive and an unusable Allocate Tasks step.
   */
  allocationKey: string;
  taskId: string;
  taskName: string;
  stageId: string;
  stageName: string;
  monthWeek: string;
  plotNumber: string;
  status?: "not-started" | "in-progress" | "completed";
}

const ForecastingPage = () => {
  const currentUser = useUser();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [selectedPlots, setSelectedPlots] = React.useState<Set<string>>(
    new Set()
  );
  const [bulkStageSelections, setBulkStageSelections] = React.useState<
    IBulkStageSelection[]
  >([]);
  const [projectCosts, setProjectCosts] = React.useState<IProjectCostInput>({
    estimatedLabourCost: 0,
    estimatedPlantCost: 0,
    estimatedMaterialCost: 0,
    estimatedSubcontractorCost: 0,
    labourCosts: [],
    plantCosts: [],
    materialCosts: [],
    subcontractorCosts: []
  });
  const [variations, setVariations] = React.useState<
    {
      id: string;
      variation: string;
      utilizedAmount: number;
      availableAmount: number;
      totalAmount: number;
    }[]
  >([]);
  // Allocations are parked in a ref so that drag-and-drop inside
  // `AllocateTasksStep` never re-renders the wizard: that step owns the
  // reactive copy and swaps this ref to it on every change. `taskAllocations`
  // is the flat snapshot the later steps read, refreshed on each step change
  // (`goToStep`).
  const weeklyAllocationsRef = React.useRef<WeeklyAllocations>(new Map());
  const [taskAllocations, setTaskAllocations] = React.useState<
    ITaskAllocation[]
  >([]);
  const [expandedSections, setExpandedSections] = React.useState<{
    labour: boolean;
    plant: boolean;
    materials: boolean;
    subcontractors: boolean;
  }>({
    labour: true,
    plant: true,
    materials: true,
    subcontractors: true
  });

  const [summaryExpandedSections, setSummaryExpandedSections] = React.useState<{
    projectCostDetails: boolean;
    plotDetails: boolean;
  }>({
    projectCostDetails: true,
    plotDetails: true
  });

  const reforecastInitRef = React.useRef(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { opportunityNo, projectDesc, month } = useParams();

  const carryForwardState = location.state as ICarryForwardState | null;
  const carryForwardTasks = carryForwardState?.carryForwardTasks ?? [];
  const copyPreviousCosts = !!carryForwardState?.copyPreviousCosts;
  const hasCarryForward = carryForwardTasks.length > 0;

  const {
    isLoading: monthForecastLoading,
    data: monthForecastData,
    error: monthForecastError
  } = useGetMonthlyForecast(
    projectDesc,
    month!.slice(0, 4),
    month!.slice(-2),
    opportunityNo
  );
  const {
    isLoading: projectCentralRepoLoading,
    data: projectCentralRepoData,
    error: projectCentralRepoError
  } = useGetProjectCentralRepoWithOpportunityNo(opportunityNo);
  const { isLoading: jobTypesLoading, data: jobTypes } =
    useGetProjectJobTypes(opportunityNo);

  // Nothing is saved until Submit, so leaving the wizard part-way loses it.
  const allowLeave = useConfirmLeave({
    when: !!projectCentralRepoData,
    title: "Leave forecasting?",
    content:
      "Are you sure you want to leave? Your changes to this forecast won't be saved."
  });

  const createMonthlyForecast = useCreateMonthlyForecast();
  const createNewMonthlyForecastVersion = useCreateNewMonthlyForecastVersion();
  const lockUnlockForecast = useLockUnlockForecast();

  const monthForecast404 =
    monthForecastError && (monthForecastError as AxiosError)?.status === 404;
  const projectCentralRepo404 =
    projectCentralRepoError &&
    (projectCentralRepoError as AxiosError)?.status === 404;

  // Only groundworks is forecastable, so a Roads & Sewers-only project has no
  // central-repo record and 404s exactly like a project whose tender was never
  // submitted. The job-types API is what tells the two apart, so the "submit
  // the tender" message isn't shown to someone who has nothing to submit.
  const isRoadsAndSewersOnly =
    !!jobTypes?.roadsAndSewers && !jobTypes.groundworks;

  const isReforecasting =
    !monthForecastLoading && !monthForecast404 && !!monthForecastData;

  // A new forecast copies the previous month's costs when the user kept that
  // option ticked in the overview's carry-forward modal.
  const previousMonth = dayjs(month).subtract(1, "month");
  const { data: previousMonthForecast } = useGetMonthlyForecast(
    monthForecast404 && copyPreviousCosts ? projectDesc : undefined,
    previousMonth.format("YYYY"),
    previousMonth.format("MM"),
    opportunityNo
  );

  const costSource = isReforecasting
    ? monthForecastData
    : previousMonthForecast;

  useForecastCostPrefill({
    source: costSource,
    includeVariations: isReforecasting,
    setProjectCosts,
    setVariations
  });

  // Preselect plots, stages, and task allocations from previous forecast when reforecasting
  React.useEffect(() => {
    if (!isReforecasting || !monthForecastData) return;

    // Extract unique plots from previous forecast
    const previousPlots = new Set<string>();
    monthForecastData.groundworks?.StagesAndTasks?.forEach((entry) => {
      if (entry.Plot) {
        previousPlots.add(entry.Plot);
      }
    });
    if (previousPlots.size > 0) {
      setSelectedPlots(previousPlots);
    }

    // Build stage map from previous forecast entries
    const stageMap = new Map<string, Set<string>>();
    monthForecastData.groundworks?.StagesAndTasks?.forEach((entry) => {
      if (entry.Stage && entry.Plot) {
        if (!stageMap.has(entry.Stage)) {
          stageMap.set(entry.Stage, new Set());
        }
        stageMap.get(entry.Stage)!.add(entry.Plot);
      }
    });

    // Stage selections and task allocations both require projectCentralRepoData to check
    // completion status — wait for it before setting either.
    if (projectCentralRepoData) {
      const taskStatusMap = new Map<string, string>();
      projectCentralRepoData.groundworks?.plots?.forEach((plot) => {
        plot.stages?.forEach((stage) => {
          stage.tasks?.forEach((task) => {
            taskStatusMap.set(task.taskId, task.currentStatus);
          });
        });
      });

      // Pre-select stages, skipping any plot-stage combination where all tasks are completed
      if (stageMap.size > 0) {
        const selections: IBulkStageSelection[] = [];
        stageMap.forEach((plotNumbers, stageId) => {
          const nonCompletedPlots = [...plotNumbers].filter((plotNumber) => {
            const plot = projectCentralRepoData.groundworks?.plots?.find(
              (p) => p.plotNo === plotNumber
            );
            const stageData = plot?.stages?.find(
              (s) => s.stageName === stageId
            );
            const tasks = stageData?.tasks ?? [];
            if (tasks.length === 0) return true;
            const completedCount = tasks.filter(
              (t) => t.currentStatus === "Completed"
            ).length;
            return completedCount < tasks.length;
          });
          if (nonCompletedPlots.length > 0) {
            selections.push({ stageId, plotNumbers: nonCompletedPlots });
          }
        });
        setBulkStageSelections(selections);
      }

      // Pre-populate task allocations into week dropzones (skip completed tasks)
      const allocations: ITaskAllocation[] = [];
      monthForecastData.groundworks?.StagesAndTasks?.forEach((entry) => {
        if (!entry.SelectedWeek || !entry.Plot || !entry.Stage) return;

        const monthWeek = `${month}-W${entry.SelectedWeek.replace("W", "")}`;

        entry.Tasks?.forEach((task) => {
          const centralStatus = taskStatusMap.get(task.taskId);
          if (centralStatus === "Completed") return;

          allocations.push({
            allocationKey: getTaskAllocationKey({
              taskId: task.taskId,
              stageId: entry.Stage,
              plotNumber: entry.Plot!
            }),
            taskId: task.taskId,
            taskName: task.taskName,
            stageId: entry.Stage,
            stageName: entry.Stage,
            monthWeek,
            plotNumber: entry.Plot!,
            status: "not-started"
          });
        });
      });

      if (allocations.length > 0) {
        weeklyAllocationsRef.current = groupAllocationsByWeek(allocations);
        reforecastInitRef.current = true;
      }
    }
  }, [isReforecasting, monthForecastData, projectCentralRepoData, month]);

  // Pre-populate plots and stages from carry-forward tasks (new forecast only)
  React.useEffect(() => {
    if (!hasCarryForward || isReforecasting) return;

    // Extract unique plots
    const cfPlots = new Set<string>();
    carryForwardTasks.forEach((t) => {
      if (t.plot) cfPlots.add(t.plot);
    });
    if (cfPlots.size > 0) {
      setSelectedPlots(cfPlots);
    }

    // Extract stage-to-plots mapping
    const stageMap = new Map<string, Set<string>>();
    carryForwardTasks.forEach((t) => {
      if (t.stage && t.plot) {
        if (!stageMap.has(t.stage)) {
          stageMap.set(t.stage, new Set());
        }
        stageMap.get(t.stage)!.add(t.plot);
      }
    });

    if (stageMap.size > 0) {
      const selections: IBulkStageSelection[] = [];
      stageMap.forEach((plotNumbers, stageId) => {
        selections.push({ stageId, plotNumbers: [...plotNumbers] });
      });
      setBulkStageSelections(selections);
    }
  }, [hasCarryForward, isReforecasting]);

  // Clean up task allocations when stage selections change. This handles the
  // case where the user goes back, changes plots/stages, and the previously
  // allocated tasks are no longer valid. `AllocateTasksStep` is unmounted while
  // stages are being edited, so it picks the pruned map up when it remounts.
  React.useEffect(() => {
    // Skip cleanup on the first run after reforecast pre-population
    // to avoid wiping out the task allocations we just set
    if (reforecastInitRef.current) {
      reforecastInitRef.current = false;
      return;
    }

    // Build a set of valid task keys from current bulkStageSelections
    const validKeys = new Set<string>();
    bulkStageSelections.forEach((selection) => {
      selection.plotNumbers.forEach((plotNumber) => {
        const plot = projectCentralRepoData?.groundworks?.plots?.find(
          (p) => p.plotNo === plotNumber
        );
        const stageData = plot?.stages?.find(
          (s) => s.stageName === selection.stageId
        );
        stageData?.tasks?.forEach((task) => {
          validKeys.add(
            getTaskAllocationKey({
              taskId: task.taskId,
              stageId: selection.stageId,
              plotNumber
            })
          );
        });
      });
    });

    weeklyAllocationsRef.current = pruneAllocations(
      weeklyAllocationsRef.current,
      validKeys
    );
  }, [bulkStageSelections, projectCentralRepoData]);

  const handlePlotSelectionChange = (plots: Set<string>) => {
    setSelectedPlots(plots);

    // Prune bulkStageSelections to remove plot numbers that are no longer selected
    setBulkStageSelections((prev) => {
      const pruned = prev
        .map((selection) => ({
          ...selection,
          plotNumbers: selection.plotNumbers.filter((p) => plots.has(p))
        }))
        .filter((selection) => selection.plotNumbers.length > 0);

      if (
        pruned.length !== prev.length ||
        pruned.some(
          (s, i) => s.plotNumbers.length !== prev[i].plotNumbers.length
        )
      ) {
        return pruned;
      }
      return prev;
    });
  };

  const toggleSection = (
    section: "labour" | "plant" | "materials" | "subcontractors"
  ) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const toggleSummarySection = (
    section: "projectCostDetails" | "plotDetails"
  ) => {
    setSummaryExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const steps = [
    {
      title: "Select Runs/Plots",
      content: (
        <SelectRunPlotStep
          projectCentralRepoData={projectCentralRepoData!}
          monthForecastData={monthForecastData}
          selectedPlots={selectedPlots}
          handlePlotSelectionChange={handlePlotSelectionChange}
        />
      )
    },
    {
      title: "Add Stages",
      content: (
        <AddStagesStep
          projectCentralRepoData={projectCentralRepoData!}
          monthForecastData={monthForecastData}
          selectedPlots={[...selectedPlots]}
          bulkStageSelections={bulkStageSelections}
          setBulkStageSelections={setBulkStageSelections}
        />
      )
    },
    {
      title: "Allocate Tasks",
      content: (
        <AllocateTasksStep
          projectCentralRepoData={projectCentralRepoData!}
          monthForecastData={monthForecastData}
          selectedMonth={month}
          bulkStageSelections={bulkStageSelections}
          weeklyAllocationsRef={weeklyAllocationsRef}
          carryForwardTasks={carryForwardTasks}
        />
      )
    },
    {
      title: "Add Cost/Duration",
      content: (
        <AddCostDurationStep
          projectCosts={projectCosts}
          setProjectCosts={setProjectCosts}
          variations={variations}
          setVariations={setVariations}
          expandedSections={expandedSections}
          toggleSection={toggleSection}
          projectCentralRepoData={projectCentralRepoData!}
          taskAllocations={taskAllocations}
          monthForecastData={monthForecastData}
          isPrefilled={!!costSource}
          opportunityNo={opportunityNo}
        />
      )
    },
    {
      title: "Summary",
      content: (
        <SummaryStep
          projectCentralRepoData={projectCentralRepoData!}
          projectCosts={projectCosts}
          variations={variations}
          bulkStageSelections={bulkStageSelections}
          selectedPlots={selectedPlots}
          taskAllocations={taskAllocations}
          summaryExpandedSections={summaryExpandedSections}
          toggleSummarySection={toggleSummarySection}
          selectedMonth={month}
        />
      )
    }
  ];

  const goToStep = (step: number) => {
    // `AllocateTasksStep` writes straight into the ref, so refresh the snapshot
    // the remaining steps read whenever the wizard moves.
    setTaskAllocations(flattenAllocations(weeklyAllocationsRef.current));
    setCurrentStep(step);
  };

  const handlePrevious = () => {
    goToStep(Math.max(currentStep - 1, 0));
  };

  const isSubmitting =
    createMonthlyForecast.isPending ||
    createNewMonthlyForecastVersion.isPending ||
    lockUnlockForecast.isPending;

  const handleNext = () => {
    if (currentStep === 0 && selectedPlots.size === 0) {
      message.warning("Please select at least one plot");
      return;
    }

    if (currentStep === 1 && bulkStageSelections.length === 0) {
      message.warning("Please add at least one stage to plots");
      return;
    }

    if (currentStep === 2 && weeklyAllocationsRef.current.size === 0) {
      message.warning("Please allocate at least one task to a week");
      return;
    }

    if (currentStep === 3) {
      const hasBlankLabour = projectCosts.labourCosts.some(
        (l) => l.id.startsWith("_new_") || !l.name
      );
      const hasBlankPlant = projectCosts.plantCosts.some(
        (p) => p.id.startsWith("_new_") || !p.name
      );
      const hasBlankSubcontractor = projectCosts.subcontractorCosts.some(
        (s) => !s.name
      );
      const hasBlankVariation = variations.some(
        (v) => !v.variation || v.utilizedAmount <= 0
      );

      if (
        hasBlankLabour ||
        hasBlankPlant ||
        hasBlankSubcontractor ||
        hasBlankVariation
      ) {
        const sections: string[] = [];
        if (hasBlankVariation) sections.push("Variation");
        if (hasBlankLabour) sections.push("Labour");
        if (hasBlankPlant) sections.push("Plant");
        if (hasBlankSubcontractor) sections.push("Subcontractor");
        message.warning(
          `Please fill in or remove blank ${sections.join(", ")} entries before proceeding`
        );
        return;
      }

      if (getForecastTotal(projectCosts, variations) === 0) {
        Modal.confirm({
          title: "Forecast total is £0.00",
          icon: <ExclamationCircleOutlined />,
          content:
            "You haven't added any costs to this forecast. Are you sure you want to continue?",
          okText: "Continue",
          cancelText: "Go back",
          centered: true,
          onOk: () => goToStep(4)
        });
        return;
      }
    }

    goToStep(Math.min(currentStep + 1, 4));
  };

  const handleComplete = async () => {
    if (!projectCentralRepoData) {
      message.error("Project data not available");
      return;
    }

    const updatedByEmail = currentUser?.email || "";

    // Build selectedTaskIds from taskAllocations
    const selectedTaskIds = taskAllocations.map((task) => ({
      taskId: task.taskId,
      selectedWeek: task.monthWeek.slice(-2)
    }));

    const materialAmount = projectCosts.materialCosts[0]?.amount ?? 0;
    const budgetMaterialCost = {
      percent: getMaterialPercent(
        materialAmount,
        projectCosts.estimatedMaterialCost
      ),
      amount: materialAmount
    };

    if (isReforecasting) {
      // Group tasks by week + plot + stage combination
      const stageMap: Record<
        string,
        {
          id: number;
          selectedWeek: string;
          plot: string;
          stage: string;
          qaStatus: string;
          stageForecastedCosts: {
            forecastedLabourCosts: number;
            forecastedPlantCost: number;
            forecastedMaterialCost: number;
            forecastedSubContractorCost: number;
          };
          tasks: { taskId: string; taskName: string; status: string }[];
        }
      > = {};

      let idCounter = 1;
      taskAllocations.forEach((task) => {
        const selectedWeek = task.monthWeek.slice(-2);
        const key = `${selectedWeek}-${task.plotNumber}-${task.stageId}`;

        if (!stageMap[key]) {
          // Look up stage data from central repo for qaStatus and forecasted costs
          const plotData = projectCentralRepoData.groundworks?.plots?.find(
            (p) => p.plotNo === task.plotNumber
          );
          const stageData = plotData?.stages?.find(
            (s) => s.stageName === task.stageId
          );

          stageMap[key] = {
            id: idCounter++,
            selectedWeek: `${selectedWeek}`,
            plot: task.plotNumber,
            stage: task.stageId,
            qaStatus: "",
            stageForecastedCosts: {
              forecastedLabourCosts:
                stageData?.forecastedCostsAggregate?.forecastedLabourCosts || 0,
              forecastedPlantCost:
                stageData?.forecastedCostsAggregate?.forecastedPlantCost || 0,
              forecastedMaterialCost:
                stageData?.forecastedCostsAggregate?.forecastedMaterialCost ||
                0,
              forecastedSubContractorCost:
                stageData?.forecastedCostsAggregate
                  ?.forecastedSubContractorCost || 0
            },
            tasks: []
          };
        }

        stageMap[key].tasks.push({
          taskId: task.taskId,
          taskName: task.taskName,
          status: "Not Started"
        });
      });

      const data = {
        opportunityNo,
        projectName: projectCentralRepoData.projectName,
        date: month!,
        payload: {
          groundworks: {
            stagesAndTasks: Object.values(stageMap)
          },
          roadAndSewers: {
            stagesAndTasks: []
          },
          budgetForecasts: {
            budgetLabourCosts: projectCosts.labourCosts.map((labour) => ({
              id: labour.id,
              name: labour.name,
              ratePerDay: getLabourDayRate(labour),
              ratePerHour: labour.ratePerHour,
              hoursPerDay: labour.hoursPerDay,
              noOfDays: labour.noOfDays,
              amount: labour.amount
            })),
            budgetPlantCosts: projectCosts.plantCosts.map((plant) => ({
              id: plant.id,
              name: plant.name,
              unitOfMeasure: plant.unitOfMeasure,
              unitCost: plant.unitCost,
              quantity: plant.quantity,
              noOfDays: plant.noOfDays,
              amount: plant.amount
            })),
            budgetMaterialCost,
            budgetSubContractorCost: projectCosts.subcontractorCosts.map(
              (sub) => ({
                name: sub.name,
                amount: sub.amount
              })
            )
          },
          variations:
            variations?.map((v) => ({
              variation: v.variation,
              utilizedAmount: v.utilizedAmount,
              availableAmount: v.availableAmount,
              totalAmount: v.totalAmount
            })) || [],
          updatedBy: updatedByEmail
        }
      };

      try {
        await createNewMonthlyForecastVersion.mutateAsync(data as any);
        await lockUnlockForecast.mutateAsync({
          opportunityNo,
          projectName: projectCentralRepoData!.projectName,
          date: month!,
          payload: {
            isLocked: true,
            lockedBy: updatedByEmail,
            lockReason: "Reforecasting"
          }
        });
        message.success("Reforecast created successfully");
        allowLeave();
        navigate("/dashboard");
      } catch (error) {
        message.error("Failed to reforecast");
      }
    } else {
      const payload = {
        projectId: projectCentralRepoData.projectId,
        projectName: projectCentralRepoData.projectName,
        opportunityNo,
        month: month!, // Format: YYYY-MM
        selectedTaskIds,
        budgetForecasts: {
          budgetLabourCosts: projectCosts.labourCosts.map((labour) => ({
            id: labour.id,
            name: labour.name,
            ratePerDay: getLabourDayRate(labour),
            ratePerHour: labour.ratePerHour,
            hoursPerDay: labour.hoursPerDay,
            noOfDays: labour.noOfDays,
            amount: labour.amount
          })),
          budgetPlantCosts: projectCosts.plantCosts.map((plant) => ({
            id: plant.id,
            name: plant.name,
            unitOfMeasure: plant.unitOfMeasure,
            unitCost: plant.unitCost,
            quantity: plant.quantity,
            noOfDays: plant.noOfDays,
            amount: plant.amount
          })),
          budgetMaterialCost,
          budgetSubContractorCost: projectCosts.subcontractorCosts.map(
            (sub) => ({
              name: sub.name,
              amount: sub.amount
            })
          )
        },
        weeklyForecast: {},
        variations:
          variations?.map((v) => ({
            variation: v.variation,
            utilizedAmount: v.utilizedAmount,
            availableAmount: v.availableAmount,
            totalAmount: v.totalAmount
          })) || []
      };

      try {
        await createMonthlyForecast.mutateAsync(payload);
        message.success("Monthly forecast created successfully");
        allowLeave();
        navigate("/dashboard");
      } catch (error) {
        message.error("Failed to create monthly forecast");
      }
    }
  };

  return (
    <Layout>
      <Skeleton loading={monthForecastLoading}>
        <div className="p-2">
          <PageHeader
            title={
              monthForecast404 ? "Monthly Forecasting" : "Monthly Reforecasting"
            }
            description={
              monthForecast404
                ? "Create and manage monthly forecasts with detailed cost breakdown"
                : "Update existing monthly forecast with new data"
            }
          />
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
            <Text strong>
              Project: <Text>{projectDesc}</Text>
            </Text>
            <Text strong>
              Month:&nbsp;
              <Text>{dayjs(month).format("MMMM YYYY")}</Text>
            </Text>
          </div>
        </div>

        <div className="mt-6 p-4 bg-white rounded-lg">
          <Steps
            current={currentStep}
            items={steps.map((step) => ({ title: step.title }))}
            labelPlacement="vertical"
            className="[&_.ant-steps-item-title]:leading-tight"
          />
        </div>

        <div className="mt-6 bg-white p-4 rounded-lg">
          <Skeleton loading={projectCentralRepoLoading || jobTypesLoading}>
            {projectCentralRepoData && (
              <>
                <div className="mb-6">{steps[currentStep].content}</div>

                <div className="-mx-4 h-px bg-[#f3f4f6]" />

                <div className="flex justify-between mt-4">
                  <Button
                    type="text"
                    size="large"
                    onClick={handlePrevious}
                    disabled={currentStep === 0 || isSubmitting}
                  >
                    Previous
                  </Button>
                  {currentStep < steps.length - 1 ? (
                    <Button
                      type="primary"
                      size="large"
                      icon={<ArrowRightOutlined />}
                      iconPosition="end"
                      onClick={handleNext}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      type="primary"
                      size="large"
                      icon={<CheckCircleOutlined />}
                      loading={isSubmitting}
                      onClick={handleComplete}
                    >
                      Submit Forecast
                    </Button>
                  )}
                </div>
              </>
            )}

            {projectCentralRepo404 &&
              (isRoadsAndSewersOnly ? (
                <p className="text-center">
                  Forecasting is not yet available for Roads &amp; Sewers
                  projects. Only Groundworks projects can be forecasted at the
                  moment.
                </p>
              ) : (
                <p className="text-center">
                  Forecasting is unavailable because a Tender has not been
                  submitted for this Project. Please ensure the Tender is
                  submitted and is under &quot;Won&quot; status.
                </p>
              ))}
          </Skeleton>
        </div>
      </Skeleton>
    </Layout>
  );
};

export default ForecastingPage;

// Mock data for labour types and persons
export const mockLabourTypes = [
  { id: "labour-type-1", name: "General Labourer" },
  { id: "labour-type-2", name: "Skilled Labourer" },
  { id: "labour-type-3", name: "Foreman" },
  { id: "labour-type-4", name: "Supervisor" }
];

export const mockLabourPersons = [
  {
    id: "person-1",
    name: "John Smith",
    labourTypeId: "labour-type-1",
    dailyRate: 150
  },
  {
    id: "person-2",
    name: "Mike Johnson",
    labourTypeId: "labour-type-2",
    dailyRate: 200
  },
  {
    id: "person-3",
    name: "Dave Williams",
    labourTypeId: "labour-type-3",
    dailyRate: 250
  },
  {
    id: "person-4",
    name: "Tom Brown",
    labourTypeId: "labour-type-4",
    dailyRate: 300
  },
  {
    id: "person-5",
    name: "James Wilson",
    labourTypeId: "labour-type-1",
    dailyRate: 160
  },
  {
    id: "person-6",
    name: "Robert Davis",
    labourTypeId: "labour-type-2",
    dailyRate: 220
  }
];

export const mockPlantTypes = [
  { id: "plant-1", name: "Excavator", dailyRate: 500 },
  { id: "plant-2", name: "Dumper", dailyRate: 300 },
  { id: "plant-3", name: "Roller", dailyRate: 350 },
  { id: "plant-4", name: "Crane", dailyRate: 800 },
  { id: "plant-5", name: "Concrete Mixer", dailyRate: 250 }
];

export const mockSubcontractorTypes = [
  { id: "sub-1", name: "Groundworks Ltd" },
  { id: "sub-2", name: "Drainage Solutions" },
  { id: "sub-3", name: "Paving Contractors" },
  { id: "sub-4", name: "Utilities Connect" }
];
